import type { ProposalInput, ProposalOutput } from "../types";

type ProductType =
  | "consumer"
  | "marketplace"
  | "fintech"
  | "health"
  | "logistics"
  | "commerce"
  | "saas";

type ConfidenceLevel = "high" | "medium" | "low";

export type AgencyPricingTierId = "starter" | "growth" | "scale";

export interface AgencyPricingTier {
  id: AgencyPricingTierId;
  name: string;
  low: number;
  high: number;
  rangeLabel: string;
  summary: string;
}

export interface PricingAssessment {
  recommendedTierId: AgencyPricingTierId;
  recommendedTierLabel: string;
  recommendedRange: {
    low: number;
    high: number;
    label: string;
  };
  packages: AgencyPricingTier[];
  rangeExplanation: string;
  riskNote: string;
  confidenceLevel: ConfidenceLevel;
  confidenceLabel: string;
  confidenceScore: number;
  rationale: string[];
}

export interface PricingEngineInput {
  idea?: string;
  inputs?: Partial<ProposalInput> | null;
  output?: Partial<ProposalOutput> | null;
}

/**
 * Pricing tuning knobs.
 * Keep this centralized so commercial assumptions can be adjusted quickly without touching UI rendering logic.
 */
const PRICING_TUNING = {
  currencyStep: 500,
  baseMvpAnchor: 43000,
  minAgencyQuote: 30000,
  maxAgencyQuote: 260000,
  defaultTimelineWeeks: 8,
  packageMultipliers: {
    starter: 0.62,
    growth: 1.14,
    scale: 1.98,
  },
  rangeGuards: {
    starter: {
      minLow: 30000,
      minHigh: 45000,
      maxHigh: 60000,
      minWidth: 12000,
    },
    growth: {
      minLow: 60000,
      minHigh: 90000,
      maxHigh: 125000,
      minWidth: 18000,
    },
    scale: {
      minLow: 110000,
      minHigh: 160000,
      maxHigh: 230000,
      minWidth: 24000,
    },
  },
} as const;

const CORE_STACK_WORDS = ["react native", "firebase", "cloud functions", "typescript"];
const INTEGRATION_KEYWORDS = [
  "stripe",
  "paypal",
  "shopify",
  "salesforce",
  "hubspot",
  "slack",
  "zapier",
  "twilio",
  "sendgrid",
  "segment",
  "mixpanel",
  "maps",
  "mapbox",
  "google maps",
  "intercom",
  "analytics",
  "webhook",
  "api integration",
  "oauth",
  "sso",
];
const AI_KEYWORDS = ["ai", "llm", "gpt", "openai", "anthropic", "cv", "computer vision", "ml", "inference", "vector"];
const AUTH_KEYWORDS = ["auth", "login", "sign in", "signup", "user account", "profile", "session", "password", "oauth"];
const ADMIN_KEYWORDS = ["admin", "dashboard", "back office", "operator panel", "moderation", "cms"];
const BACKEND_KEYWORDS = ["workflow", "queue", "sync", "realtime", "role", "permissions", "reporting", "analytics", "event"];
const POLISH_KEYWORDS = ["pixel", "animation", "micro-interaction", "brand", "production-ready", "launch", "qa", "accessibility"];
const UNCERTAINTY_KEYWORDS = ["unknown", "unclear", "depends", "investigate", "assume", "risky", "uncertain", "third-party"];
const COMPLEX_FEATURE_KEYWORDS = ["real-time", "offline", "recommendation", "scheduling", "payments", "multi-step", "approval", "matching"];

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function roundMoney(value: number): number {
  return Math.round(value / PRICING_TUNING.currencyStep) * PRICING_TUNING.currencyStep;
}

export function formatUsd(value: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);
}

function normalizeText(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function normalizeStringList(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((item) => normalizeText(item)).filter(Boolean);
  }
  if (typeof value === "string") {
    return value
      .split(/\n|,/)
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
}

function normalizeFeatures(value: unknown): Array<{ title: string; desc: string; priority: "MVP" | "Next" }> {
  if (!Array.isArray(value)) return [];
  return value
    .map((feature): { title: string; desc: string; priority: "MVP" | "Next" } => ({
      title: normalizeText((feature as any)?.title),
      desc: normalizeText((feature as any)?.desc),
      priority: (feature as any)?.priority === "Next" ? "Next" : "MVP",
    }))
    .filter((feature) => feature.title || feature.desc);
}

function normalizeDelivery(value: unknown): Array<{ phase: string; weeks: number; outcomes: string[] }> {
  if (!Array.isArray(value)) return [];
  return value
    .map((phase, index) => ({
      phase: normalizeText((phase as any)?.phase) || `Phase ${index + 1}`,
      weeks: Math.max(1, Math.round(Number((phase as any)?.weeks) || 1)),
      outcomes: normalizeStringList((phase as any)?.outcomes),
    }))
    .filter((phase) => phase.phase);
}

function normalizePackages(value: unknown): Array<{ name: string; price: number }> {
  if (!Array.isArray(value)) return [];
  return value
    .map((pkg, index) => ({
      name: normalizeText((pkg as any)?.name) || `Package ${index + 1}`,
      price: Number((pkg as any)?.price) > 0 ? Math.round(Number((pkg as any)?.price)) : 0,
    }))
    .filter((pkg) => pkg.name);
}

function containsAny(text: string, keywords: string[]): boolean {
  return keywords.some((keyword) => text.includes(keyword));
}

function countKeywordHits(text: string, keywords: string[]): number {
  return keywords.reduce((count, keyword) => (text.includes(keyword) ? count + 1 : count), 0);
}

function inferProductType(text: string): { type: ProductType; multiplier: number; label: string } {
  if (containsAny(text, ["finance", "bank", "lending", "payment", "wallet"])) {
    return { type: "fintech", multiplier: 1.24, label: "regulated or payment-sensitive product context" };
  }
  if (containsAny(text, ["health", "medical", "patient", "clinic", "wellness"])) {
    return { type: "health", multiplier: 1.18, label: "health-adjacent user and data requirements" };
  }
  if (containsAny(text, ["marketplace", "matching", "buyer", "seller", "booking"])) {
    return { type: "marketplace", multiplier: 1.2, label: "two-sided workflow complexity" };
  }
  if (containsAny(text, ["delivery", "fleet", "driver", "route", "tracking"])) {
    return { type: "logistics", multiplier: 1.16, label: "operational workflow and tracking needs" };
  }
  if (containsAny(text, ["commerce", "ecommerce", "cart", "checkout", "store"])) {
    return { type: "commerce", multiplier: 1.12, label: "commerce and conversion-critical journeys" };
  }
  if (containsAny(text, ["b2b", "saas", "workspace", "team", "ops"])) {
    return { type: "saas", multiplier: 1.1, label: "multi-role product behavior" };
  }
  return { type: "consumer", multiplier: 1.04, label: "consumer-facing MVP baseline" };
}

function parseTimelineWeeks(inputTimeline: string): number | null {
  const match = inputTimeline.match(/(\d+)\s*(week|weeks|wk|wks)/i);
  if (!match) return null;
  const parsed = Number(match[1]);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function getTimelineWeeks(deliveryPlan: Array<{ weeks: number }>, timelineInput: string): number {
  if (deliveryPlan.length) {
    return deliveryPlan.reduce((sum, phase) => sum + Math.max(1, phase.weeks), 0);
  }
  const parsedTimeline = parseTimelineWeeks(timelineInput);
  if (parsedTimeline) return parsedTimeline;
  return PRICING_TUNING.defaultTimelineWeeks;
}

function buildRange(anchor: number, spread: number) {
  const rawLow = anchor * (1 - spread);
  const rawHigh = anchor * (1 + spread);
  let low = roundMoney(rawLow);
  let high = roundMoney(rawHigh);
  low = Math.max(low, PRICING_TUNING.minAgencyQuote);
  high = Math.min(high, PRICING_TUNING.maxAgencyQuote);
  if (high - low < 10000) {
    high = Math.min(PRICING_TUNING.maxAgencyQuote, low + 10000);
  }
  if (high < low) {
    high = low;
  }
  return {
    low,
    high,
    label: `${formatUsd(low)} – ${formatUsd(high)}`,
  };
}

function guardTierRange(tierId: AgencyPricingTierId, lowInput: number, highInput: number) {
  const guard = PRICING_TUNING.rangeGuards[tierId];
  let low = roundMoney(Math.max(PRICING_TUNING.minAgencyQuote, lowInput));
  let high = roundMoney(Math.min(PRICING_TUNING.maxAgencyQuote, highInput));

  low = Math.max(low, guard.minLow);
  high = Math.max(high, guard.minHigh);
  high = Math.min(high, guard.maxHigh);

  if (high - low < guard.minWidth) {
    high = Math.min(guard.maxHigh, roundMoney(low + guard.minWidth));
    if (high - low < guard.minWidth) {
      low = Math.max(guard.minLow, roundMoney(high - guard.minWidth));
    }
  }

  if (high < low) high = low;

  return {
    low,
    high,
    label: `${formatUsd(low)} – ${formatUsd(high)}`,
  };
}

function defaultTierSummaries(hasAi: boolean, integrationCount: number): Record<AgencyPricingTierId, string> {
  return {
    starter:
      integrationCount > 1 || hasAi
        ? "A focused MVP path built to validate the product with disciplined scope and selective integrations."
        : "A focused MVP path built to validate core value with a smaller, high-quality first release.",
    growth:
      integrationCount > 2 || hasAi
        ? "A production product path for teams that need serious launch readiness, deeper flows, and reliable integrations."
        : "A production product path for teams that want a credible first release with launch-grade quality.",
    scale:
      hasAi
        ? "A strategic build partner path for complex products with advanced AI workflows, deeper architecture, and long-term scale."
        : "A strategic build partner path for ambitious products with expanded operations, resilience, and long-term scale.",
  };
}

export function buildPricingAssessment(input: PricingEngineInput): PricingAssessment {
  const idea = normalizeText(input.idea || input.inputs?.idea);
  const output = input.output ?? {};
  const features = normalizeFeatures(output.keyFeatures);
  const deliveryPlan = normalizeDelivery(output.deliveryPlan);
  const techStack = normalizeStringList(output.techStack);
  const risks = normalizeStringList(output.risksMitigations);
  const pricingPackages = normalizePackages(output.pricingPackages);
  const timelineInput = normalizeText(input.inputs?.timeline);
  const platforms = Array.isArray(input.inputs?.platforms) ? input.inputs?.platforms : [];

  const featureText = features.map((feature) => `${feature.title} ${feature.desc}`).join(" ");
  const stackText = techStack.join(" ");
  const riskText = risks.join(" ");
  const packageText = pricingPackages.map((pkg) => pkg.name).join(" ");
  const corpus = `${idea} ${featureText} ${stackText} ${riskText} ${packageText}`.toLowerCase();

  const productType = inferProductType(corpus);
  const mvpFeatures = features.filter((feature) => feature.priority === "MVP");
  const nextFeatures = features.filter((feature) => feature.priority === "Next");
  const weightedFeatureCount =
    (mvpFeatures.length || Math.min(features.length, 3) || 3) + Math.max(0, nextFeatures.length) * 0.65;
  const featureComplexityHits = countKeywordHits(corpus, COMPLEX_FEATURE_KEYWORDS);
  const featureComplexityScore = clamp(1 + weightedFeatureCount * 0.11 + featureComplexityHits * 0.08, 1, 2.2);
  const featureComplexityMultiplier = 1 + (featureComplexityScore - 1) * 0.46;

  const integrationHitsFromText = countKeywordHits(corpus, INTEGRATION_KEYWORDS);
  const explicitIntegrationCount = techStack
    .map((item) => item.toLowerCase())
    .filter((item) => item && !CORE_STACK_WORDS.some((coreWord) => item.includes(coreWord))).length;
  const integrationCount = Math.max(0, Math.max(explicitIntegrationCount, integrationHitsFromText));
  const integrationMultiplier = 1 + Math.min(0.42, integrationCount * 0.08 + (integrationCount > 2 ? 0.06 : 0));

  const aiHits = countKeywordHits(corpus, AI_KEYWORDS);
  const aiLevel = aiHits >= 3 ? 2 : aiHits >= 1 ? 1 : 0;
  const aiMultiplier = aiLevel === 2 ? 1.28 : aiLevel === 1 ? 1.13 : 1;

  const hasAuth = containsAny(corpus, AUTH_KEYWORDS) || features.some((feature) => feature.title.toLowerCase().includes("profile"));
  const authMultiplier = hasAuth ? 1.06 : 1;

  const backendHits = countKeywordHits(corpus, BACKEND_KEYWORDS);
  const backendMultiplier = 1 + Math.min(0.26, backendHits * 0.05 + integrationCount * 0.02);

  const adminNeeds = containsAny(corpus, ADMIN_KEYWORDS);
  const adminMultiplier = adminNeeds ? 1.09 : 1;

  const platformCount = platforms?.length || 0;
  const hasWeb = platforms?.includes("web") || false;
  const platformMultiplier =
    platformCount >= 3 || (platformCount >= 2 && hasWeb) ? 1.2 : platformCount >= 2 ? 1.12 : 1.05;

  const timelineWeeks = getTimelineWeeks(deliveryPlan, timelineInput);
  const urgencyMultiplier = timelineWeeks <= 6 ? 1.16 : timelineWeeks <= 8 ? 1.08 : timelineWeeks <= 12 ? 1.02 : 1;
  const urgencySpread = timelineWeeks <= 6 ? 0.04 : timelineWeeks <= 8 ? 0.02 : 0;

  const polishHits = countKeywordHits(corpus, POLISH_KEYWORDS);
  const polishMultiplier = polishHits >= 3 ? 1.14 : polishHits >= 1 ? 1.08 : 1.03;

  const uncertaintyHits = countKeywordHits(corpus, UNCERTAINTY_KEYWORDS);
  const uncertaintyMultiplier = 1 + Math.min(0.18, uncertaintyHits * 0.04);

  let anchor =
    PRICING_TUNING.baseMvpAnchor *
    productType.multiplier *
    featureComplexityMultiplier *
    integrationMultiplier *
    aiMultiplier *
    authMultiplier *
    backendMultiplier *
    adminMultiplier *
    platformMultiplier *
    urgencyMultiplier *
    polishMultiplier *
    uncertaintyMultiplier;

  const manualPackageAnchors = pricingPackages
    .map((pkg) => pkg.price)
    .filter((price) => price >= PRICING_TUNING.minAgencyQuote * 0.5 && price <= PRICING_TUNING.maxAgencyQuote * 1.2)
    .sort((a, b) => a - b);

  if (manualPackageAnchors.length) {
    const middleIndex = Math.floor(manualPackageAnchors.length / 2);
    const medianAnchor = manualPackageAnchors[middleIndex];
    anchor = anchor * 0.82 + medianAnchor * 0.18;
  }

  anchor = clamp(anchor, PRICING_TUNING.minAgencyQuote, PRICING_TUNING.maxAgencyQuote);

  const spread = clamp(
    0.18 + uncertaintyHits * 0.04 + Math.max(0, integrationCount - 1) * 0.02 + aiLevel * 0.03 + urgencySpread,
    0.18,
    0.42
  );

  const complexityIndex =
    (featureComplexityMultiplier + integrationMultiplier + aiMultiplier + backendMultiplier + polishMultiplier + platformMultiplier) /
    6;
  const recommendedTierId: AgencyPricingTierId = complexityIndex >= 1.46 ? "scale" : complexityIndex >= 1.14 ? "growth" : "starter";

  const tierSummaries = defaultTierSummaries(aiLevel > 0, integrationCount);
  const tierRanges: AgencyPricingTier[] = (["starter", "growth", "scale"] as AgencyPricingTierId[]).map((tierId) => {
    const tierAnchor = anchor * PRICING_TUNING.packageMultipliers[tierId];
    const tierSpread = clamp(
      tierId === "starter" ? spread * 0.85 : tierId === "scale" ? spread * 1.12 : spread,
      0.16,
      0.45
    );
    const rawRange = buildRange(tierAnchor, tierSpread);
    const range = guardTierRange(tierId, rawRange.low, rawRange.high);
    const tierName =
      tierId === "starter"
        ? "Foundation / Focused MVP"
        : tierId === "growth"
          ? "Product / Production Product"
          : "Strategic / Build Partner";
    return {
      id: tierId,
      name: tierName,
      low: range.low,
      high: range.high,
      rangeLabel: range.label,
      summary: tierSummaries[tierId],
    };
  });

  const recommendedTier = tierRanges.find((tier) => tier.id === recommendedTierId) ?? tierRanges[1];

  const signalCoverage = [
    features.length > 0,
    deliveryPlan.length > 0,
    techStack.length > 0,
    risks.length > 0,
    Boolean(timelineInput),
    platformCount > 0,
  ].filter(Boolean).length;
  const confidenceScore = clamp(
    Math.round(58 + signalCoverage * 7 - uncertaintyHits * 6 - aiLevel * 5 - (timelineWeeks <= 6 ? 4 : 0)),
    45,
    90
  );
  const confidenceLevel: ConfidenceLevel = confidenceScore >= 78 ? "high" : confidenceScore >= 62 ? "medium" : "low";
  const confidenceLabel =
    confidenceLevel === "high"
      ? "Executive confidence: strong scope clarity"
      : confidenceLevel === "medium"
        ? "Executive confidence: good scope clarity"
        : "Executive confidence: emerging scope clarity";

  const topDrivers: string[] = [];
  if (featureComplexityMultiplier >= 1.22) topDrivers.push("feature depth");
  if (integrationCount > 0) topDrivers.push(integrationCount > 2 ? "multi-system integrations" : "integration scope");
  if (aiLevel > 0) topDrivers.push(aiLevel === 2 ? "advanced AI workflows" : "AI-assisted capabilities");
  if (adminNeeds) topDrivers.push("admin and operational tooling");
  if (timelineWeeks <= 8) topDrivers.push("accelerated delivery tempo");
  if (!topDrivers.length) topDrivers.push("core MVP foundation");

  const rangeExplanation =
    `This investment range is based on ${productType.label}, ${topDrivers.slice(0, 3).join(", ")}, ` +
    `and the delivery shape required for a high-quality release.`;

  const riskNote =
    uncertaintyHits >= 3 || aiLevel === 2
      ? "Planning guidance: this range is intentionally wider because technical scope still has material unknowns."
      : uncertaintyHits > 0 || integrationCount >= 3
        ? "Planning guidance: this range includes contingency for integration and implementation unknowns typically validated in discovery."
        : "Planning guidance: scope is relatively clear, so the investment band is tighter than average at this stage.";

  const rationale = [
    `Scope baseline: assumes a serious MVP baseline (Home, Core Feature, Profile) with agency-grade architecture and QA.`,
    `Feature depth: ${Math.max(3, mvpFeatures.length)} core user journeys with ${nextFeatures.length} secondary flows shape delivery effort.`,
    `Integrations and backend: ${integrationCount > 0 ? `${integrationCount} meaningful integration touchpoint${integrationCount === 1 ? "" : "s"}` : "light external integrations"} plus backend orchestration and data reliability.`,
    `Delivery confidence: ${confidenceLabel.toLowerCase()} based on available scope detail${uncertaintyHits ? " and identified unknowns" : ""}.`,
    `Quality bar: includes launch-quality UX polish, release hardening, and practical post-launch stability work.`,
  ];

  return {
    recommendedTierId: recommendedTier.id,
    recommendedTierLabel: recommendedTier.name,
    recommendedRange: {
      low: recommendedTier.low,
      high: recommendedTier.high,
      label: recommendedTier.rangeLabel,
    },
    packages: tierRanges,
    rangeExplanation,
    riskNote,
    confidenceLevel,
    confidenceLabel,
    confidenceScore,
    rationale,
  };
}
